import { Module } from 'app/library/module/models/module.model';
import { ScheduledJob } from 'app/library/scheduler/models/scheduled-job.model';
import { UserExtendedAttributeWithValue } from 'app/projects/user/src/lib/models/user-attribute-with-value';
import { CourseStructureCategoryBreadcrumb } from 'app/library/course-offerings-category/models/course-structure-category-breadcrumb.model';
import { CompletionRule } from '../enums/completion-rule.enum';
import { BookingApprovalType } from '../enums/booking-approval-type.enum';
import { ICourseOfferingQuery } from '../interfaces/course-offering.query.interface';
import { IOfferingGroupQuery } from '../interfaces/group.query.interface';
import { CourseTemplate } from './course-template.model';
import { OfferingGroup } from './group.model';
import { LinkedAudience } from './linked-audience.model';
import { PostingType } from 'app/library/posting/models/posting-type.model';

// @dynamic
export class CourseOffering {
    id: string;
    name: string;
    bookingUrl: string;
    sort: number;
    courseTemplate: CourseTemplate;
    category: { id: string };
    offeringGroup?: { id: string };
    productCode: string;
    executionCount: number;
    nextExecution: { startDate: Date };
    runningExecutionCount: number;
    type: number;
    completionRule: CompletionRule;
    bookingApprovalType: BookingApprovalType;
    linkedAudiences: LinkedAudience[] = [];
    audience: {
        id: string;
        name: string;
        breadcrumbs: string;
    };
    schedules: ScheduledJob[];
    attributes: UserExtendedAttributeWithValue[];
    language: string;
    contentType: PostingType;
    price: {
        amount: number | null;
        currency: string | null;
    };
    breadcrumbs: CourseStructureCategoryBreadcrumb[];
    breadcrumbLabel: string;

    isSelected: boolean; // Frontend property
    alreadyAdded: boolean; // Frontend property
    courseOfferingExecutions: Module[]; // Frontend property
    isLoading: boolean; // Frontend property

    constructor(offering?: ICourseOfferingQuery, offeringGroup?: IOfferingGroupQuery) {
        if (offering) {
            this.id = offering.id.toString();
            this.name = offering.name;
            this.bookingUrl = offering.booking_url;
            this.sort = offering.sort;
            this.category = offering.category ? { id: offering.category.id.toString() } : null;
            this.courseTemplate = new CourseTemplate(offering.course_template);
            this.offeringGroup = offeringGroup ? new OfferingGroup(offeringGroup) : null;
            this.productCode = offering.product_code;
            this.nextExecution = { startDate: offering.next_execution?.start_date };
            this.executionCount = offering.execution_count;
            this.runningExecutionCount = offering.running_execution_count;
            this.type = offering.type;
            this.completionRule = offering.completion_rule;
            this.bookingApprovalType = offering.booking_approval_type;
            this.language = offering.language;
            this.contentType = offering.content_type ? new PostingType(offering.content_type) : null;
            this.price = {
                amount: offering.price?.amount ?? null,
                currency: offering.price?.currency ?? null,
            };
            this.attributes = offering.attributes ? offering.attributes.map((query) => new UserExtendedAttributeWithValue(query.attribute, query.value)) : [];
            this.breadcrumbs = offering.breadcrumbs
                ? offering.breadcrumbs.map((breadcrumb) => new CourseStructureCategoryBreadcrumb(breadcrumb))
                : [];
            this.breadcrumbLabel = this.breadcrumbs.map((breadcrumb) => breadcrumb.name).join(' / ');

            if (offering.audience) {
                this.audience = {
                    id: offering.audience.id.toString(),
                    name: offering.audience.name,
                    breadcrumbs: offering.audience.breadcrumbs,
                };
            }

            if (offering.linked_audiences?.length) {
                this.linkedAudiences = offering.linked_audiences.map((linkedAudience) => new LinkedAudience(linkedAudience));
            }
        } else {
            this.id = null;
            this.name = null;
            this.bookingUrl = null;
            this.sort = null;
            this.category = null;
            this.courseTemplate = null;
            this.offeringGroup = null;
            this.productCode = null;
            this.nextExecution = null;
            this.executionCount = null;
            this.runningExecutionCount = null;
            this.type = null;
            this.completionRule = null;
            this.bookingApprovalType = null;
            this.audience = null;
            this.linkedAudiences = null;
            this.attributes = null;
            this.breadcrumbs = [];
            this.breadcrumbLabel = '';
            this.price = {
                amount: null,
                currency: null,
            };
        }
    }
}
